/**
 * Yup validation schema — VER-109 full-power survey editor.
 *
 * Enforces the structural invariants the runner + scoring rely on, and gates
 * publishing: a survey cannot be published until `surveyEditorSchema` passes.
 *
 *   - non-empty code / title / instructions
 *   - ≥ 2 shared-scale options (each a non-empty label + numeric value)
 *   - ≥ 1 section, each with a title and ≥ 1 question
 *   - each question: non-empty text, a valid type; type-specific rules:
 *       · multi → its own ≥ 2 numeric options
 *       · vas   → min < max
 *   - branching rules reference a source question + a target, with a numeric value
 *   - optional multiplier (positive) + optional cut-off (label required)
 */
import * as yup from "yup";
import type { SurveyDefinition } from "./survey.types";

export const scaleOptionSchema = yup.object({
    label: yup.string().trim().required("Option label is required"),
    value: yup
        .number()
        .typeError("Value must be a number")
        .required("Value is required")
        .integer("Value must be a whole number"),
});

export const questionSchema = yup.object({
    id: yup.string().required(),
    order: yup.number().required(),
    text: yup.string().trim().required("Question text is required"),
    type: yup
        .string()
        .oneOf(["likert", "single", "multi", "freeText", "vas", "nps"], "Unknown question type")
        .required(),
    reverseScored: yup.boolean().required(),
    scored: yup.boolean().required(),
    required: yup.boolean().required(),
    options: yup
        .array()
        .of(scaleOptionSchema)
        .when("type", {
            is: "multi",
            then: (schema) => schema.min(2, "Add at least two options").required("Multi-choice needs options"),
            otherwise: (schema) => schema.notRequired(),
        }),
    vas: yup.object().when("type", {
        is: "vas",
        then: () =>
            yup.object({
                min: yup.number().typeError("Min must be a number").required("Min is required"),
                max: yup
                    .number()
                    .typeError("Max must be a number")
                    .required("Max is required")
                    .moreThan(yup.ref("min"), "Max must be greater than min"),
                minLabel: yup.string().nullable(),
                maxLabel: yup.string().nullable(),
                step: yup.number().positive().nullable(),
            }),
        otherwise: () => yup.object().nullable().notRequired(),
    }),
    freeText: yup.object().notRequired(),
});

export const sectionSchema = yup.object({
    id: yup.string().required(),
    order: yup.number().required(),
    title: yup.string().trim().required("Section title is required"),
    description: yup.string().nullable(),
    questions: yup
        .array()
        .of(questionSchema)
        .min(1, "Each section needs at least one question")
        .required("Each section needs at least one question"),
});

export const branchingRuleSchema = yup.object({
    id: yup.string().required(),
    sourceQuestionId: yup.string().required("Choose a question to branch on"),
    operator: yup.string().oneOf(["eq", "neq", "gt", "gte", "lt", "lte"]).required(),
    value: yup.number().typeError("Comparison value must be a number").required("A comparison value is required"),
    action: yup.string().oneOf(["show", "hide"]).required(),
    targetType: yup.string().oneOf(["question", "section"]).required(),
    targetId: yup.string().required("Choose a target"),
});

export const surveyEditorSchema = yup.object({
    code: yup.string().trim().required("A short code is required (e.g. WHO5)"),
    title: yup.string().trim().required("Title is required"),
    instructions: yup.string().trim().required("Instructions are required"),
    higherIsBetter: yup.boolean().required(),
    defaultScale: yup.object({
        options: yup
            .array()
            .of(scaleOptionSchema)
            .min(2, "Add at least two scale options")
            .required("At least two scale options are required"),
    }),
    sections: yup
        .array()
        .of(sectionSchema)
        .min(1, "Add at least one section")
        .required("At least one section is required"),
    branchingRules: yup.array().of(branchingRuleSchema).default([]),
    scoring: yup.object({
        method: yup.string().oneOf(["sum"]).required(),
        multiplier: yup
            .number()
            .typeError("Multiplier must be a number")
            .positive("Multiplier must be greater than zero")
            .nullable()
            .transform((value, original) => (original === "" || original === null ? undefined : value)),
        cutoff: yup
            .object({
                belowValue: yup.number().typeError("Threshold must be a number").required("Threshold is required"),
                label: yup.string().trim().required("Guidance label is required"),
            })
            .nullable()
            .default(undefined),
    }),
    licence: yup.string().trim().nullable(),
});

/** True when the survey satisfies every publish invariant. Never throws. */
export const isSurveyValid = (survey: SurveyDefinition): boolean => surveyEditorSchema.isValidSync(survey);
